import * as React from 'react';
import { createNoCacheExtraImageUrl } from './image.util';
import RaisedButton from 'material-ui/RaisedButton';
import Popover from 'material-ui/Popover';
import Menu from 'material-ui/Menu';
import MenuItem from 'material-ui/MenuItem';
import ActionCameraEnhance from 'material-ui/svg-icons/action/camera-enhance';
import { white, yellow500 } from 'material-ui/styles/colors';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import * as Webcam from 'react-webcam';
import Avatar from 'material-ui/Avatar';

const VALID_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.bmp', '.gif', '.png'];

type Props = {
  readonly defaultImageUrl: string
  readonly customMaxHeight?: string
  readonly onImageFileChangeHandler?: (fileFormatError: string, fileSizeError: string, file?: File) => void
  readonly unselectImageHandler?: () => void
  readonly mode?: string
  readonly isAvatar?: boolean
};

type States = {
  readonly file?: File
  readonly previousFile?: File
  readonly imagePreviewUrl?: string
  readonly previousImagePreviewUrl?: string
  readonly fileSizeError: string
  readonly fileFormatError: string
  readonly selectOpen: boolean
  readonly anchorEl?: any
  readonly openCamModal: boolean
  readonly screenshot: string
};

class ImageUploadComponent extends React.Component<Props, States> {

  private selectFileEvent: HTMLInputElement;

  constructor(props: Props) {
    super(props);

    this.state = {
      file: undefined,
      previousFile: undefined,
      imagePreviewUrl: '',
      previousImagePreviewUrl: '',
      fileSizeError: '',
      fileFormatError: '',
      selectOpen: false,
      anchorEl: null,
      openCamModal: false,
      screenshot: ''
    };

    this.handleImageChange = this.handleImageChange.bind(this);
    this.imageChangeCallback = this.imageChangeCallback.bind(this);
    this.handleUnselectImage = this.handleUnselectImage.bind(this);
  }

  render() {
    const { customMaxHeight } = this.props;
    return (
      <div>
        {this.renderPictureSection()}
        <Dialog
          title="Take A Picture"
          actions={[
            this.renderCancelButton(() => this.setState({openCamModal: false})),
            this.renderSubmitButton(this.takePic)
          ]}
          modal={true}
          open={this.state.openCamModal}
          onRequestClose={() => this.setState({openCamModal: false})}
        >
          <div className="row">
            <div className="col-12 text-center" style={{ maxHeight: (customMaxHeight) ? +customMaxHeight : 250}}>
              {
                <Webcam 
                  ref="webcam"
                  audio={false}
                  screenshotFormat="image/jpeg"
                  style={{ width: '100%', height: '100%' }}
                />
              }
            </div>
          </div>
        </Dialog>
      </div>
    );
  }

  private renderPictureSection = (): JSX.Element => {
    const { imagePreviewUrl } = this.state;
    const { isAvatar, customMaxHeight, defaultImageUrl } = this.props;

    return (
      (isAvatar) ?
      (
        <div>
          <Avatar
            size={(customMaxHeight) ? +customMaxHeight : 250}
            src={(imagePreviewUrl) ?
              imagePreviewUrl :
              defaultImageUrl + createNoCacheExtraImageUrl(defaultImageUrl)}
          />
          {
            this.props.mode && this.props.mode.toUpperCase() === 'DISPLAY' ?
              null :
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                }}
              >
                <Avatar
                  icon={<ActionCameraEnhance />}
                  color={white}
                  backgroundColor={yellow500}
                  size={50}
                  onClick={this.handleUpdateClick}
                  className="hover-pointer"
                  style={{
                    position: 'relative',
                    top: (customMaxHeight) ? +customMaxHeight / 4 : 65,
                    left: (customMaxHeight) ? +customMaxHeight / 4 : 65
                  }}
                />
                <Popover
                  open={this.state.selectOpen}
                  anchorEl={this.state.anchorEl}
                  anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
                  targetOrigin={{horizontal: 'left', vertical: 'top'}}
                  onRequestClose={this.handleRequestClose}
                  autoCloseWhenOffScreen={true}
                >
                  <Menu>
                    <MenuItem
                      primaryText="Select An Image"
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        this.setState({selectOpen: false});
                        this.selectFileEvent.click();
                      }}
                    />
                    <MenuItem
                      primaryText="Take A Picture" 
                      onClick={this.openCamModal}
                    />
                  </Menu>
                </Popover>
                <input
                  ref={(ref: HTMLInputElement) => this.selectFileEvent = ref}
                  type="file"
                  onChange={this.handleImageChange}
                  style={{display: 'none'}}
                />
              </div>
          }
        </div>
      ) : (
        <div>
          <div className="row">
            <div className="col-12" style={{textAlign: 'center'}}>
              {this.renderImagePreview()}
            </div>
          </div>
          {
            this.props.mode && this.props.mode.toUpperCase() === 'DISPLAY' ?
              null :
              this.renderSelectButton()
          }
        </div>
      )
    );
  }

  private takePic = (): void => {
    this.setState(
      {
        screenshot: (this.refs.webcam as any).getScreenshot(),
      },
      () => {
        const facePic = this.dataURLtoFile();

        if (facePic) {
          this.setState(
            {
              file: facePic,
              imagePreviewUrl: this.state.screenshot,
              openCamModal: false
            },
            this.imageChangeCallback
          );
        }
      }
    );
  }

  private dataURLtoFile = (): File | null => {
    const arr = this.state.screenshot.split(',');

    if (arr.length > 0) {
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], 'face-image.jpg', {type: mime});
    } else {
      return null;
    }
  }

  private renderSubmitButton = (onClickFunction: (e: React.SyntheticEvent<{}>) => void): JSX.Element => {
    return(
      <FlatButton
        label="Submit"
        primary={true}
        keyboardFocused={true}
        onClick={onClickFunction}
      />
    );
  }

  private renderCancelButton = (onClickFunction: (e: React.SyntheticEvent<{}>) => void): JSX.Element => {
    return(
      <FlatButton
        label="Cancel"
        primary={true}
        onClick={onClickFunction}
      />
    );
  }

  private renderSelectButton = (): JSX.Element => {
    return(
      <div className="row pa2">
        <div className="col-12" style={{textAlign: 'center'}}>
          <RaisedButton
            primary={true}
            style={{minWidth: '200px'}}
            label={'UPDATE'}
            icon={<ActionCameraEnhance color={white} hoverColor={yellow500} />}
            onClick={this.handleUpdateClick}
          />
          <Popover
            open={this.state.selectOpen}
            anchorEl={this.state.anchorEl}
            anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
            targetOrigin={{horizontal: 'left', vertical: 'top'}}
            onRequestClose={this.handleRequestClose}
            autoCloseWhenOffScreen={true}
          >
            <Menu>
              <MenuItem
                primaryText="Select An Image"
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  this.setState({selectOpen: false});
                  this.selectFileEvent.click();
                }}
              />
              <MenuItem
                primaryText="Take A Picture" 
                onClick={this.openCamModal}
              />
            </Menu>
          </Popover>
        </div>
        <input
          ref={(ref: HTMLInputElement) => this.selectFileEvent = ref}
          type="file"
          onChange={this.handleImageChange}
          style={{display: 'none'}}
        />
      </div>
    );
  }

  private openCamModal = (): void => this.setState({openCamModal: true, selectOpen: false});

  private handleUpdateClick = (event: React.SyntheticEvent<{}>): void =>
    this.setState({
      selectOpen: true,
      anchorEl: event.currentTarget
    })

  private handleRequestClose = (): void => this.setState({selectOpen: false});

  private handleImageChange = (e: React.FormEvent<HTMLInputElement>): void => {
    e.preventDefault();

    if (e.currentTarget.value.length !== 0) {
      let reader = new FileReader();
      const file: File | undefined = e.currentTarget.files && e.currentTarget.files.length > 0 ?
        e.currentTarget.files.item(0) : 
        undefined;

      const valid = VALID_FILE_EXTENSIONS.map((v: string) => {
        if (file) {
          return file.name.substr(file.name.length - v.length, v.length).toLowerCase() === v.toLowerCase();
        } else {
          return false;
        }
      }).includes(true);

      if (!valid) {
        return this.setState(
          {fileFormatError: 'File must be an image.'},
          this.imageChangeCallback
        );
      }

      if (file !== undefined && file.size > (1024 * 1024)) {
        return this.setState(
          {fileSizeError: 'File size must be less than 1MB'},
          this.imageChangeCallback
        );
      } else if (file) {
        reader.onloadend = () => {
          this.setState(
            {
              file: file,
              fileSizeError: '',
              imagePreviewUrl: reader.result
            },
            this.imageChangeCallback
          );
        };
    
        reader.readAsDataURL(file);
      }
    }
  }

  private handleUnselectImage = (): void => {
    const { file, imagePreviewUrl } = this.state;
    const { unselectImageHandler } = this.props;

    this.setState(
      {
        previousFile: file,
        imagePreviewUrl: imagePreviewUrl,
        fileSizeError: ''
      },
      () => this.setState({imagePreviewUrl: '', file: undefined}, unselectImageHandler)
    );
  }

  private imageChangeCallback = (): void => {
    const { onImageFileChangeHandler } = this.props;
    const { file, fileSizeError, fileFormatError } = this.state;

    if (onImageFileChangeHandler) {
      onImageFileChangeHandler(fileFormatError, fileSizeError, file);
    }
  }

  private renderImagePreview = (): JSX.Element => {
    const { imagePreviewUrl } = this.state;
    const { customMaxHeight, defaultImageUrl } = this.props;

    if (imagePreviewUrl) {
      return(
        <img 
          src={imagePreviewUrl}
          style={{
            maxHeight: (customMaxHeight) ? `${customMaxHeight}px` : '480px'
          }}
        />
      );
    } else {
      return(
        <img 
          src={defaultImageUrl + createNoCacheExtraImageUrl(defaultImageUrl)}
          style={{
            maxHeight: (customMaxHeight) ? `${customMaxHeight}px` : '480px'
          }}
        />
      );
    }
  }
}

export default ImageUploadComponent;


// WEBPACK FOOTER //
// ./src/util/ImageUploadComponent.tsx